import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import { extractErrorMessage } from '../../shared/utils/errors';
import {
  getPlatformPairRate,
  getPlatformRates,
  PlatformPairRate,
  PlatformRateRow,
  PlatformRates,
} from './api';
import PlatformRateDetailModal from './PlatformRateDetailModal';

const SOURCES: { key: 'binance' | 'bybit' | 'okx'; label: string }[] = [
  { key: 'binance', label: 'Binance' },
  { key: 'bybit', label: 'ByBit' },
  { key: 'okx', label: 'OKX' },
];

const formatRate = (value: number | string | null) => {
  const num = value != null ? Number(value) : null;
  if (num === null || !Number.isFinite(num)) return null;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });
};

// Pair rates span many orders of magnitude (INR/BTC ~ 1e-7, BTC/INR ~ 1e7),
// so tiny values get enough decimals to stay meaningful.
const formatPairRate = (value: number | null | undefined) => {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 0.001) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    });
  }
  const decimals = Math.min(12, Math.max(2, -Math.floor(Math.log10(abs)) + 5));
  return value.toFixed(decimals);
};

/**
 * Finance -> Platform Rate.
 *
 * The single exchange rate used by the whole platform (P2P and Swap). The
 * accounting service pulls live prices from Binance, ByBit and OKX and
 * averages them: Average rate = (Binance + ByBit + OKX) / 3. Sources that
 * fail are excluded from the average; when all three are down CoinGecko and
 * the last persisted rates act as fallbacks.
 */
const PlatformRatePage: React.FC = () => {
  const [data, setData] = useState<PlatformRates | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRate, setSelectedRate] = useState<PlatformRateRow | null>(null);

  const [pairBase, setPairBase] = useState('USD');
  const [pairQuote, setPairQuote] = useState('USDT');
  const [pairData, setPairData] = useState<PlatformPairRate | null>(null);
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);

  const load = useCallback(async (mode: 'initial' | 'manual' | 'auto') => {
    if (mode === 'initial') setIsLoading(true);
    if (mode === 'manual') setIsRefreshing(true);
    try {
      const payload = await getPlatformRates(mode === 'manual');
      setData(payload);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load platform rates'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load('initial');
    const intervalId = setInterval(() => load('auto'), 10_000);
    return () => clearInterval(intervalId);
  }, [load]);

  const loadPair = useCallback(
    async (base: string, quote: string, opts: { refresh?: boolean; silent?: boolean } = {}) => {
      if (!base || !quote || base === quote) return;
      if (!opts.silent) setPairLoading(true);
      try {
        const payload = await getPlatformPairRate(base, quote, opts.refresh ?? false);
        setPairData(payload);
        setPairError(null);
      } catch (err) {
        setPairError(extractErrorMessage(err, 'Failed to load pair rate'));
      } finally {
        if (!opts.silent) setPairLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadPair(pairBase, pairQuote);
    const intervalId = setInterval(() => loadPair(pairBase, pairQuote, { silent: true }), 10_000);
    return () => clearInterval(intervalId);
  }, [pairBase, pairQuote, loadPair]);

  const cryptoCurrencies = data?.rates.map((r) => r.currency) ?? [];
  const fiatCurrencies = (data?.fiat_rates ?? []).map((r) => r.currency);

  const swapPair = () => {
    const base = pairBase;
    setPairBase(pairQuote);
    setPairQuote(base);
  };

  const sourcesUsed = data?.sources_used ?? [];
  const isFallback = data != null && data.source !== 'platform_average';

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Rate</h1>
            <p className="text-slate-400 mt-1">
              The single exchange rate used by all platform operations (P2P and Swap).
            </p>
          </div>
          <button
            type="button"
            onClick={() => load('manual')}
            disabled={isRefreshing}
            className="btn btn-secondary py-2 disabled:opacity-50"
          >
            {isRefreshing ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>

        <div className="card p-4 text-sm text-slate-400 leading-relaxed">
          <p>
            Prices are pulled live from <span className="text-white font-medium">Binance</span>,{' '}
            <span className="text-white font-medium">ByBit</span> and{' '}
            <span className="text-white font-medium">OKX</span> and averaged:
            <span className="font-mono text-slate-300"> Average rate = (Binance + ByBit + OKX) / 3</span>.
            A source that fails is excluded from the average. Swap quotes and the P2P reference
            rate read this value; the accounting service refreshes it every ~10 seconds.
          </p>
        </div>

        <div className="card p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-wide">Sources</span>
            {SOURCES.map(({ key, label }) => {
              const ok = sourcesUsed.includes(key);
              return (
                <span
                  key={key}
                  className={`badge border text-xs ${
                    ok
                      ? 'bg-green-500/10 text-green-400 border-green-500/30'
                      : 'bg-red-500/10 text-red-400 border-red-500/30'
                  }`}
                >
                  {label} {ok ? '· live' : '· unavailable'}
                </span>
              );
            })}
          </div>
        </div>

        <div className="card p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Cross rate</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              Live rate for any supported crypto/fiat pair. Crypto legs use the platform average
              (Binance/ByBit/OKX), fiat legs use the FX feed — always API data, never manual values.
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Base</label>
              <select
                value={pairBase}
                onChange={(e) => setPairBase(e.target.value)}
                className="input font-mono"
              >
                <optgroup label="Crypto">
                  {cryptoCurrencies.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </optgroup>
                <optgroup label="Fiat">
                  {fiatCurrencies.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <button
              type="button"
              onClick={swapPair}
              title="Swap direction"
              className="btn btn-secondary py-2 px-3 font-mono"
            >
              ⇄
            </button>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Quote</label>
              <select
                value={pairQuote}
                onChange={(e) => setPairQuote(e.target.value)}
                className="input font-mono"
              >
                <optgroup label="Crypto">
                  {cryptoCurrencies.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </optgroup>
                <optgroup label="Fiat">
                  {fiatCurrencies.map((currency) => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          {pairBase === pairQuote ? (
            <p className="text-sm text-amber-400">Base and quote currencies must differ.</p>
          ) : pairError ? (
            <p className="text-sm text-red-400">{pairError}</p>
          ) : pairLoading && !pairData ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <div className="w-4 h-4 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
              Fetching rate…
            </div>
          ) : pairData ? (
            <div className="space-y-2">
              <p className="text-2xl font-mono text-indigo-300 font-semibold">
                1 {pairData.base} = {formatPairRate(pairData.rate)}{' '}
                <span className="text-base text-slate-400 font-normal">{pairData.quote}</span>
              </p>
              <p className="text-sm font-mono text-slate-400">
                1 {pairData.quote} = {formatPairRate(pairData.inverse_rate)} {pairData.base}
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 pt-1">
                <span>
                  {pairData.base}: {formatPairRate(pairData.base_leg.usd)} USD
                  {' · '}
                  {pairData.base_leg.type === 'crypto'
                    ? `${pairData.crypto_source ?? '—'}${
                        (pairData.crypto_sources_used?.length ?? 0) > 0
                          ? ` (${pairData.crypto_sources_used.join(', ')})`
                          : ''
                      }`
                    : pairData.fiat_source ?? '—'}
                </span>
                <span>
                  {pairData.quote}: {formatPairRate(pairData.quote_leg.usd)} USD
                  {' · '}
                  {pairData.quote_leg.type === 'crypto'
                    ? `${pairData.crypto_source ?? '—'}${
                        (pairData.crypto_sources_used?.length ?? 0) > 0
                          ? ` (${pairData.crypto_sources_used.join(', ')})`
                          : ''
                      }`
                    : pairData.fiat_source ?? '—'}
                </span>
                <span>
                  Fetched:{' '}
                  {pairData.base_leg.fetched_at
                    ? new Date(pairData.base_leg.fetched_at).toLocaleTimeString()
                    : '—'}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !data || data.rates.length === 0 ? (
            <div className="p-12 text-center text-slate-400">No platform rates available.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">Currency</th>
                    {SOURCES.map(({ key, label }) => (
                      <th key={key} className="table-header text-right">{label}</th>
                    ))}
                    <th className="table-header text-right">Platform rate (average)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {data.rates.map((row) => (
                    <tr
                      key={row.currency}
                      className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                      onClick={() => setSelectedRate(row)}
                    >
                      <td className="table-cell">
                        <span className="text-white font-semibold">{row.currency}</span>
                      </td>
                      {SOURCES.map(({ key }) => {
                        const formatted = formatRate(row[key]);
                        return (
                          <td key={key} className="table-cell text-right font-mono text-slate-100">
                            {formatted !== null ? (
                              <>
                                {formatted} <span className="text-xs text-slate-500">USD</span>
                              </>
                            ) : (
                              <span className="text-slate-500">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="table-cell text-right font-mono">
                        {formatRate(row.average) !== null ? (
                          <span className="text-indigo-300 font-semibold">
                            {formatRate(row.average)}{' '}
                            <span className="text-xs text-slate-500 font-normal">USD</span>
                          </span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {data && (data.fiat_rates?.length ?? 0) > 0 && (
          <div className="card overflow-hidden">
            <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-white">Fiat rates</h2>
                <p className="text-sm text-slate-400 mt-0.5">
                  USD to fiat conversion rates (per 1 USD). Reference only for now.
                </p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="table-header">Currency</th>
                    <th className="table-header text-right">Rate (per 1 USD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {(data.fiat_rates ?? []).map((row) => (
                    <tr key={row.currency} className="hover:bg-slate-800/30 transition-colors">
                      <td className="table-cell">
                        <span className="text-white font-semibold">{row.currency}</span>
                      </td>
                      <td className="table-cell text-right font-mono">
                        {formatRate(row.rate) !== null ? (
                          <span className="text-indigo-300 font-semibold">
                            {formatRate(row.rate)}{' '}
                            <span className="text-xs text-slate-500 font-normal">{row.currency}</span>
                          </span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <PlatformRateDetailModal
        isOpen={selectedRate !== null}
        onClose={() => setSelectedRate(null)}
        row={selectedRate}
        sourcesUsed={sourcesUsed}
      />
    </Layout>
  );
};

export default PlatformRatePage;
